import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@shared/hooks';
import { useCustomFields } from '@features/products/hooks/useCustomFields';
import { useWarehouses } from '@features/warehouses/hooks/useWarehouseOperations';
import { useShopifyConnector } from '@features/connections/shopify/hooks/useShopifyConnector';
import { ACTIVE_IMPORT_JOBS_QUERY_KEY, formatImportTarget } from '@features/connections/hooks/useActiveImportJobs';
import { useImportJobs } from '@features/connections/hooks/useImportJobs';
import { useImportJobsStore } from '../../../../application/store/importJobsStore';
import { container } from '@infrastructure/container';
import { DEFAULT_SHOPIFY_PRODUCT_MATCH_MAPPING, ShopifyProductMatchMapping } from '@domain/connection/models/ConnectionSettings';
import { WarehouseConnectionType } from '@domain/inventory/enums/WarehouseConnectionType.enum';
import { MutationType } from '@domain/inventory/enums/MutationType.enum';
import { RuleOperator } from '@domain/inventory/enums/RuleOperator.enum';
import { RuleField } from '@domain/inventory/enums/RuleField.enum';
import ImportProgress from '@features/connections/setup/ImportProgress';
import InventoryStep from '@features/connections/setup/InventoryStep';
import { ProductPreviewProvider } from '@features/connections/setup/productPreviewContext';
import SetupFrame, { SetupStep } from '@features/connections/setup/SetupFrame';
import { SHOPIFY_MATCH_SOURCE_FIELDS } from '@shared/connection/shopify/constants/shopifyProductFields';
import { LoadingIndicator } from '@shared/ui';
import { useShopifyProductPreview } from '../hooks/useShopifyProductPreview';
import ProductsStep from './setup/ProductsStep';

type ShopifyWizardStep = 'products' | 'inventory';

const SETUP_STEPS: SetupStep[] = [
  { id: 'products', label: '1. Products' },
  { id: 'inventory', label: '2. Inventory' },
];

const ShopifyConnectionSetup = () => {
  const { id: connectionId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { connector } = useShopifyConnector();
  const { customFields: customFieldsResponse } = useCustomFields();
  const { warehouses, createWarehouse } = useWarehouses();
  const queryClient = useQueryClient();
  const addJob = useImportJobsStore(state => state.addJob);
  const { jobs: trackedJobs } = useImportJobs();

  const [step, setStep] = useState<ShopifyWizardStep>('products');
  const [productsImported, setProductsImported] = useState(false);
  const [importing, setImporting] = useState(false);
  const [productJobId, setProductJobId] = useState<string>();
  const [inventoryJobId, setInventoryJobId] = useState<string>();
  const [mapping, setMapping] = useState<ShopifyProductMatchMapping>(DEFAULT_SHOPIFY_PRODUCT_MATCH_MAPPING);
  const preview = useShopifyProductPreview({
    connectionId,
    mapping,
    onMappingChange: setMapping,
    enabled: isAuthenticated && !!connectionId,
  });
  const [loadInventory, setLoadInventory] = useState<boolean | null>(null);
  const [warehouseName, setWarehouseName] = useState('Shopify');
  const [warehouseLocation, setWarehouseLocation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const companyCustomFields = customFieldsResponse?.status === 'success' ? customFieldsResponse.data : [];
  const targetOptions = [
    { value: 'ean', label: 'EAN' },
    { value: 'sku', label: 'SKU' },
    ...companyCustomFields.map(field => ({ value: `customFields.${field.key}`, label: field.label })),
  ];

  const importsReady = useMemo(() => {
    const ids = [productJobId, inventoryJobId].filter((id): id is string => !!id);
    if (!importing || ids.length === 0) return false;
    return ids.every(id => trackedJobs.find(job => job.id === id)?.status?.state === 'completed');
  }, [importing, inventoryJobId, productJobId, trackedJobs]);

  if (authLoading) return null;
  if (!isAuthenticated) return <Navigate to="/sign-in" replace />;
  if (!connectionId) return <Navigate to="/company" replace />;

  const trackJob = (jobId: string, target: string) => {
    addJob({ id: jobId, connector: 'shopify', label: formatImportTarget(target) });
    queryClient.invalidateQueries({ queryKey: ACTIVE_IMPORT_JOBS_QUERY_KEY });
  };

  const continueProducts = () => {
    if (!SHOPIFY_MATCH_SOURCE_FIELDS.has(mapping.sourceField) || !preview.canImport) return;
    setProductsImported(true);
    setStep('inventory');
  };

  const finish = async () => {
    if (loadInventory == null) return;
    setBusy(true);
    setError(null);
    try {
      if (loadInventory) {
        await createWarehouse({
          name: warehouseName.trim() || 'Shopify',
          location: warehouseLocation.trim() || undefined,
          warehouseConnections: [{
            sourceType: WarehouseConnectionType.SHOPIFY,
            mutationType: MutationType.SYNC,
            rules: [{
              operator: RuleOperator.EQUALS,
              field: RuleField.CONNECTION_ID,
              value: connectionId,
            }],
          }],
        });
      }
      const setup = await container.shopifyConnectorRepository.completeSetup(connectionId, mapping);
      if (setup.status === 'error') throw new Error(setup.error.message ?? 'Could not import the products');
      const productJob = setup.data.jobs.find(job => job.target === 'products')?.jobId ?? setup.data.jobs[0]?.jobId;
      if (!productJob) throw new Error('The product import did not start');
      const response = await container.shopifyConnectorRepository.finishSetup(connectionId, { loadInventory });
      if (response.status === 'error') throw new Error(response.error.message);
      const inventoryJob = loadInventory ? response.data.jobs.find(job => job.target === 'inventory')?.jobId : undefined;
      if (loadInventory && !inventoryJob) throw new Error('Could not start the inventory import');
      trackJob(productJob, 'products');
      if (inventoryJob) trackJob(inventoryJob, 'inventory');
      response.data.jobs
        .filter(job => job.target === 'orders' && job.jobId)
        .forEach(job => trackJob(job.jobId, 'orders'));
      setProductJobId(productJob);
      setInventoryJobId(inventoryJob);
      setBusy(false);
      setImporting(true);
    } catch (finishError) {
      setError(finishError instanceof Error ? finishError.message : 'Could not load inventory');
      setBusy(false);
    }
  };

  const goToStep = (next: string) => {
    if (busy || importing) return;
    if (next === 'products') setStep('products');
    if (next === 'inventory' && productsImported) setStep('inventory');
  };

  const sourceCanMatch = SHOPIFY_MATCH_SOURCE_FIELDS.has(mapping.sourceField);
  const primaryLabel = step === 'inventory' ? 'Finish' : 'Next';
  const primaryDisabled = step === 'inventory'
    ? loadInventory == null || (loadInventory && !warehouseName.trim())
    : preview.loading || !sourceCanMatch || !preview.canImport;

  if (!connector) return <LoadingIndicator />;

  if (importing) {
    const description = inventoryJobId
      ? 'You can continue when products and inventory are finished.'
      : 'You can continue when the product import is finished.';
    return (
      <ImportProgress
        connector={connector}
        connectionId={connectionId}
        title="Importing your Shopify data"
        description={description}
        continueDisabled={!importsReady}
        onContinue={() => navigate('/product-catalog/products')}
      />
    );
  }

  return (
    <ProductPreviewProvider value={preview}>
      <SetupFrame
      connector={connector}
      steps={SETUP_STEPS}
      step={step}
      busy={busy}
      primaryLabel={primaryLabel}
      primaryDisabled={primaryDisabled}
      onPrimary={step === 'products' ? continueProducts : finish}
      onClose={() => navigate('/company')}
      onStepChange={goToStep}
      error={error}
    >
      {step === 'products' && (
        <ProductsStep
          mapping={mapping}
          onMappingChange={setMapping}
          targetOptions={targetOptions}
        />
      )}
      {step === 'inventory' && (
        <InventoryStep
          description="Choose whether to import inventory from Shopify."
          choiceCaption="Shopify inventory"
          loadInventory={loadInventory}
          onLoadInventoryChange={setLoadInventory}
          warehouses={(warehouses ?? []).map(warehouse => ({ id: warehouse.id, name: warehouse.name }))}
          warehouseName={warehouseName}
          warehouseLocation={warehouseLocation}
          onWarehouseNameChange={setWarehouseName}
          onWarehouseLocationChange={setWarehouseLocation}
        />
      )}
      </SetupFrame>
    </ProductPreviewProvider>
  );
};

export default ShopifyConnectionSetup;
